<template>
    <app-popover ref="popover" @close="save();popoverClosed();" :close-on-scroll="false" class="color-picker">
        <template #default>
            <input v-if="name" type="hidden" :name="name" :value="modelValue" aria-label="Farbauswahl"/>
            <div class="cp-preview flex-vertical-middle">
                <div @click="toggle" @keydown.space.prevent="toggle" tabindex="0" ref="focusElement" class="cp-preview-inner flex-center flex-vertical-middle flex" :class="{empty: !modelValue}" :style="{backgroundColor: modelValue ? '#' + modelValue : ''}">
                    <i class="material-icons" aria-hidden="true" :style="{color: iconContrastColor}" role="img">colorize</i>
                </div>
            </div>
        </template>
        <template #container>
            <div class="cp-container">
                <div class="cp-palette-container">
                    <div class="cp-palette">
                        <div v-for="paletteRow in paletteUpperCase" class="flex">
                            <span class="cp-item" v-for="{color, type, disable} in paletteRow" :class="{active: color === pick}" @click="paletteHandlerClick(color)">
                                <span class="cp-item-inner" :class="[type, {'cp-item-disabled' : disable}]" :style="{backgroundColor: '#' + color}"></span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="cp-picker-container">
                    <div class="flex">
                        <div class="cp-saturation-wrap">
                            <div class="cp-saturation"
                                :style="{background: backgroundColor}"
                                ref="saturationContainer"
                                @mousedown="saturationHandleMousedown"
                                @touchmove="saturationHandleChange"
                                @touchstart="saturationHandleChange">
                                <div class="cp-saturation-white"></div>
                                <div class="cp-saturation-black"></div>
                                <div class="cp-saturation-pointer" v-if="!isEmpty && colors" :style="{top: saturationPointerTop, left: saturationPointerLeft}">
                                    <div class="cp-saturation-circle"></div>
                                </div>
                            </div>
                        </div>
                        <div class="cp-hue-wrap">
                            <div class="cp-hue cp-hue-vertical">
                                <div class="cp-hue-container" role="slider" :aria-valuenow="colors && colors.hsl.h" aria-valuemin="0" aria-valuemax="360" ref="hueContainer"
                                    @mousedown="hueHandleMouseDown" @touchmove="hueHandleChange" @touchstart="hueHandleChange">
                                    <div class="cp-hue-pointer" v-if="colors" :style="{top: huePointerTop}" role="presentation">
                                        <div class="cp-hue-picker"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="cp-input-wrapper">
                        <input type="text" class="long" v-model="inputValue" @keyup.enter="inputChange" ref="input" aria-label="Hexadezimalfarbwert"/>
                        <span v-if="!isEmpty" v-interact="clear" class="icon-interact clear">
                            <i aria-hidden="true" class="material-icons" role="img">clear</i>
                        </span>
                    </div>
                    <br/>
                    <button class="button button-primary" @click="close">Bestätigen</button>
                    <button class="button button-default" @click="cancel">Abbrechen</button>
                </div>
            </div>
        </template>
    </app-popover>
</template>

<script lang="ts">
    import tinycolor from 'tinycolor2';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import {checkContrast, useWhite} from '../library/contrast';
    import {clamp} from '../library/math';
    import AppPopover from './AppPopover.vue';

    type ColorChangeData = string | { source: 'hsva' | 'hsl' | 'hex', h?: string | number, s?: number, l?: number, v?: number, a?: number, hex?: string };

    function _colorChange(data: any, oldHue = null)
    {
        let alpha = data && data.a;
        let color: any;

        // hsl is better than hex between conversions
        if (data && data.hsl)
        {
            color = tinycolor(data.hsl);
        }
        else if (data && data.hex && data.hex.length > 0)
        {
            color = tinycolor(data.hex);
        }
        else if (data && data.hsv)
        {
            color = tinycolor(data.hsv);
        }
        else if (data && data.rgba)
        {
            color = tinycolor(data.rgba);
        }
        else if (data && data.rgb)
        {
            color = tinycolor(data.rgb);
        }
        else
        {
            color = tinycolor(data);
        }

        if (color && (color._a === undefined || color._a === null))
        {
            color.setAlpha(alpha || 1);
        }

        let hsl = color.toHsl();
        let hsv = color.toHsv();

        if (data && hsl.s === 0)
        {
            let value = data.h || (data.hsl && data.hsl.h) || oldHue || 0;
            hsv.h     = value;
            hsl.h     = value;
        }

        /* https://github.com/xiaokaike/vue-color/
        // --- comment this block to fix #109, may cause #25 again ---
        // when the hsv.v is less than 0.0164 (base on test)
        // because of possible loss of precision
        // the result of hue and saturation would be miscalculated
        // if (hsv.v < 0.0164) {
        //   hsv.h = data.h || (data.hsv && data.hsv.h) || 0
        //   hsv.s = data.s || (data.hsv && data.hsv.s) || 0
        // }

        // if (hsl.l < 0.01) {
        //   hsl.h = data.h || (data.hsl && data.hsl.h) || 0
        //   hsl.s = data.s || (data.hsl && data.hsl.s) || 0
        // }
        // ------
         */

        return {
            hsl: hsl,
            hex: color.toHexString().toUpperCase(),
            hex8: color.toHex8String().toUpperCase(),
            rgba: color.toRgb(),
            hsv: hsv,
            oldHue: data.h || oldHue || hsl.h,
            source: data.source,
            a: data.a || color.getAlpha(),
        };
    }

    const colorRegExp  = /[0-9a-f]{6}/i;
    const colorPalette = [
        ['#000000', '#212121', '#424242', '#616161', '#707070', '#9e9e9e', '#bdbdbd', '#e0e0e0', '#fafafa', '#ffffff'],
        ['#263238', '#37474f', '#455a64', '#546e7a', '#607d8b', '#78909c', '#90a4ae', '#b0bec5', '#cfd8dc', '#eceff1'],
        ['#3e2723', '#4e342e', '#5d4037', '#6d4c41', '#795548', '#8d6e63', '#a1887f', '#bcaaa4', '#d7ccc8', '#efebe9'],
        ['#bf360c', '#d84315', '#e64a19', '#f4511e', '#ff5722', '#ff7043', '#ff8a65', '#ffab91', '#ffccbc', '#fbe9e7'],
        ['#e65100', '#ef6c00', '#f57c00', '#fb8c00', '#ff9800', '#ffa726', '#ffb74d', '#ffcc80', '#ffe0b2', '#fff3e0'],
        ['#ff6f00', '#ff8f00', '#ffa000', '#ffb300', '#ffc107', '#ffca28', '#ffd54f', '#ffe082', '#ffecb3', '#fff8e1'],
        ['#f57f17', '#f9a825', '#fbc02d', '#fdd835', '#ffeb3b', '#ffee58', '#fff176', '#fff59d', '#fff9c4', '#fffde7'],
        ['#827717', '#9e9d24', '#afb42b', '#c0ca33', '#cddc39', '#d4e157', '#dce775', '#e6ee9c', '#f0f4c3', '#f9fbe7'],
        ['#33691e', '#558b2f', '#689f38', '#7cb342', '#8bc34a', '#9ccc65', '#aed581', '#c5e1a5', '#dcedc8', '#f1f8e9'],
        ['#1b5e20', '#2e7d32', '#388e3c', '#43a047', '#4caf50', '#66bb6a', '#81c784', '#a5d6a7', '#c8e6c9', '#e8f5e9'],
        ['#004d40', '#00695c', '#00796b', '#00897b', '#009688', '#26a69a', '#4db6ac', '#80cbc4', '#b2dfdb', '#e0f2f1'],
        ['#006064', '#00838f', '#0097a7', '#00acc1', '#00bcd4', '#26c6da', '#4dd0e1', '#80deea', '#b2ebf2', '#e0f7fa'],
        ['#01579b', '#0277bd', '#0288d1', '#039be5', '#03a9f4', '#29b6f6', '#4fc3f7', '#81d4fa', '#b3e5fc', '#e1f5fe'],
        ['#0d47a1', '#1565c0', '#1976d2', '#1e88e5', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb', '#e3f2fd'],
        ['#1a237e', '#283593', '#303f9f', '#3949ab', '#3f51b5', '#5c6bc0', '#7986cb', '#9fa8da', '#c5cae9', '#e8eaf6'],
        ['#311b92', '#4527a0', '#512da8', '#5e35b1', '#673ab7', '#7e57c2', '#9575cd', '#b39ddb', '#d1c4e9', '#ede7f6'],
        ['#4a148c', '#6a1b9a', '#7b1fa2', '#8e24aa', '#9c27b0', '#ab47bc', '#ba68c8', '#ce93d8', '#e1bee7', '#f3e5f5'],
        ['#880e4f', '#ad1457', '#c2185b', '#d81b60', '#e91e63', '#ec407a', '#f06292', '#f48fb1', '#f8bbd0', '#fce4ec'],
        ['#b71c1c', '#c62828', '#d32f2f', '#e53935', '#f44336', '#ef5350', '#e57373', '#ef9a9a', '#ffcdd2', '#ffebee'],
    ];

    @Component({
        name: 'ColorPicker',
        components: {AppPopover},
        emits: ['update:modelValue'],
    })
    export default class ColorPicker extends Vue
    {
        readonly $refs: {
            focusElement: HTMLElement
            hueContainer: HTMLElement
            input: HTMLInputElement
            popover: AppPopover
            saturationContainer: HTMLElement
        };

        @Prop({
                type: Array,
                default()
                {
                    return colorPalette;
                },
            },
        ) palette: [];

        @Prop({type: String}) modelValue: string;

        /**
         * name for input field (if required)
         */
        @Prop({type: String}) name: string;

        val = _colorChange(this.modelValue || '');

        isEmpty = !this.modelValue;

        value = this.modelValue ? this.val.hex.substr(1) : null;

        currentValue = this.value;

        inputValue = this.value;

        @Prop({
            type: String,
            default: 'none',
            validator(value: string): boolean
            {
                return ['none', 'light', 'dark'].includes(value);
            },
        }) disableType: 'none' | 'light' | 'dark';

        @Prop({
            type: String,
            default: '',
            validator(value: string): boolean
            {
                return !value || /#?[0-9a-z]{6}/i.test(value);
            },
        }) contrastTo: string;

        @Prop({
            type: Boolean,
            default: false,
        }) preventDisableClick: boolean;

        @Watch('inputValue')
        watchInputValue()
        {
            if (this.isValidColor(this.inputValue))
            {
                this.colorChange(_colorChange(this.inputValue || ''));
            }
        }

        @Watch('modelValue')
        watchValue(newValue: string)
        {
            this.isEmpty = !newValue;
            if (!this.isEmpty)
            {
                this.colors = _colorChange(newValue);
                this.value  = this.val.hex.substr(1);
            }
            else
            {
                this.value = null;
            }
        }

        get pick()
        {
            if (this.isEmpty)
            {
                return '';
            }

            return this.colors.hex.substr(1).toUpperCase();
        }

        set colors(newVal)
        {
            this.val = newVal;
            this.inputValue = this.val.hex.substr(1).toUpperCase();
        }

        get colors()
        {
            return this.val;
        }

        get paletteUpperCase()
        {
            let toUpperCase = (arr: string | string[]): string[] | object =>
            {
                if (typeof arr === 'string')
                {
                    return {
                        color: arr.substr(1).toUpperCase(),
                        type: tinycolor(arr).toHsl().l < 0.5 ? 'cp-light' : 'cp-dark',
                        disable: this.isDisabled(arr),
                    };
                }
                else
                {
                    return arr.map(color => toUpperCase(color));
                }
            };

            return toUpperCase(this.palette);
        }

        get backgroundColor()
        {
            return `hsl(${this.colors.hsv.h}, 100%, 50%)`;
        }

        get saturationPointerTop()
        {
            let top = (-(this.colors.hsv.v * 100)) + 100;
            if (top > 100)
            {
                top = 100;
            }

            return top + '%';
        }

        get saturationPointerLeft()
        {
            return this.colors.hsv.s * 100 + '%';
        }

        get huePointerTop()
        {
            return 100 - (-((this.colors.hsl.h * 100) / 360) + 100) + '%';
        }

        get iconContrastColor()
        {
            if ('string' === typeof this.value && 0 !== this.value.length)
            {
                return useWhite(this.value) ? '#ffffff' : '#666666';
            }

            return '#666666';
        }

        close()
        {
            //@ts-ignore:2339
            this.$refs.popover.close();
        }

        save()
        {
            /// update only on change
            if (this.currentValue !== this.value)
            {
                this.$emit('update:modelValue', this.value);
                this.currentValue = this.value;
            }
        }

        cancel()
        {
            this.watchValue(this.currentValue);
            this.close();
        }

        toggle()
        {
            const isOpen = this.$refs.popover.value;

            this.$refs.popover.toggle();

            window.setTimeout(() => isOpen ? this.$refs.focusElement.focus() : this.$refs.input.focus(), 50);
        }

        popoverClosed()
        {
            window.setTimeout(() => this.$refs.focusElement.focus(), 50);
        }

        clear()
        {
            this.watchValue('');
        }

        inputChange(event: KeyboardEvent)
        {
            if (event.target)
            {
                this.colorChange((event.target as HTMLInputElement).value, true);
            }
        }

        colorChange(data: ColorChangeData, save = false)
        {
            this.isEmpty    = false;
            this.colors     = _colorChange(data);
            this.value = this.val.hex.substr(1);

            if (save)
            {
                this.save();
                this.close();
            }
        }

        isDisabled(hex: string): boolean
        {
            if(this.contrastTo && !checkContrast(hex, this.contrastTo, 4.5))
            {
                return true;
            }

            if ('none' !== this.disableType)
            {
                const _useWhite: boolean = useWhite(hex);
                if (!_useWhite && 'light' === this.disableType
                    || _useWhite && 'dark' === this.disableType)
                {
                    return true;
                }
            }

            return false;
        }

        isValidColor(color: string)
        {
            return colorRegExp.test(color);
        }

        paletteHandlerClick(hex: string)
        {
            if(this.preventDisableClick && this.isDisabled(hex))
            {
                return;
            }

            this.colorChange({
                source: 'hsl',
                hex,
            }, true);
        }

        // <editor-fold desc="saturation">
        saturationHandleChange(event: any, skip = false)
        {
            !skip && event.preventDefault();

            let container       = this.$refs.saturationContainer;
            let containerWidth  = container.clientWidth;
            let containerHeight = container.clientHeight;
            let xOffset         = container.getBoundingClientRect().left + window.pageXOffset;
            let yOffset         = container.getBoundingClientRect().top + window.pageYOffset;
            let touch           = event.type.startsWith('touch') ? event?.touches[0] || event?.changedTouches[0] : null;
            let pageX           = touch && touch.pageX || event.pageX;
            let pageY           = touch && touch.pageY || event.pageY;
            let left            = clamp(pageX - xOffset, 0, containerWidth);
            let top             = clamp(pageY - yOffset, 0, containerHeight);
            let saturation      = left / containerWidth;
            let bright          = clamp(-(top / containerHeight) + 1, 0, 1);

            this.colorChange({
                h: this.colors.hsv.h,
                s: saturation,
                v: bright,
                a: this.colors.hsv.a,
                source: 'hsva',
            });
        }

        saturationHandleMousedown(event: any)
        {
            if (event.button === 0)
            {
                this.saturationHandleChange(event, true);
                window.addEventListener('mousemove', this.saturationHandleChange);
                window.addEventListener('mouseup', this.saturationHandleChange);
                window.addEventListener('mouseup', this.saturationHandleMouseup);
            }
        }

        saturationHandleMouseup()
        {
            this.saturationUnbindEventListeners();
        }

        saturationUnbindEventListeners()
        {
            window.removeEventListener('mousemove', this.saturationHandleChange);
            window.removeEventListener('mouseup', this.saturationHandleChange);
            window.removeEventListener('mouseup', this.saturationHandleMouseup);
        }

        // </editor-fold>

        // <editor-fold desc="hue">
        hueHandleChange(event: any, skip = false)
        {
            !skip && event.preventDefault();

            let container       = this.$refs.hueContainer;
            let containerHeight = container.clientHeight;
            let yOffset         = container.getBoundingClientRect().top + window.pageYOffset;
            let pageY           = event.pageY || (event.touches ? event.touches[0].pageY : 0);
            let top             = pageY - yOffset;
            let h;
            let percent;

            if (top < 0)
            {
                h = 360;
            }
            else if (top > containerHeight)
            {
                h = 0;
            }
            else
            {
                percent = -(top * 100 / containerHeight) + 100;
                h       = 360 - (360 * percent / 100);
            }

            if (this.colors.hsl.h !== h)
            {
                this.colorChange({
                    h,
                    s: this.colors.hsl.s,
                    l: this.colors.hsl.l,
                    a: this.colors.hsl.a,
                    source: 'hsl',
                });
            }
        }

        hueHandleMouseDown(event: MouseEvent)
        {
            this.hueHandleChange(event, true);
            window.addEventListener('mousemove', this.hueHandleChange);
            window.addEventListener('mouseup', this.hueHandleMouseUp);
        }

        hueHandleMouseUp()
        {
            this.hueUnbindEventListeners();
        }

        hueUnbindEventListeners()
        {
            window.removeEventListener('mousemove', this.hueHandleChange);
            window.removeEventListener('mouseup', this.hueHandleMouseUp);
        }

        // </editor-fold>
    }
</script>